import React from 'react';
import './AlertSteps.css';
import { VOLUME_GUARD_DEFAULT_MAX_MATCHES } from '../alertSpec';
import { validateGuardInputs } from '../alertPayload';
import AlertSpecProblems from './AlertSpecProblems';

/**
 * The volume guard.
 *
 * **The default must never be pre-filled into the input, and this is the
 * component that gets that right (AC11).** The read-back returns the *stored*
 * cap, not the effective one. A null cap means the author never set one and a
 * default of 25 applies at evaluation — so writing 25 into the input would
 * persist an inherited default as an explicit choice nobody made, and the alert
 * would stop tracking the default if it ever changed.
 *
 * So: stored value as the value, the default as *placeholder text only*.
 *
 * The share is entered as a percentage because that is how it is discussed, and
 * converted on the way out — the endpoint bounds the value to `(0, 1]`, so `50`
 * is refused and `0.5` is what it wants.
 */
const AlertGuardFields = ({ formData, onSetFormData }) => {
  const alert = formData.alert ?? {};
  const maxMatches = alert.volumeGuardMaxMatches;
  const maxSharePercent = alert.volumeGuardMaxShare;

  const setAlertField = (patch) => {
    onSetFormData((prev) => ({ ...prev, alert: { ...(prev.alert ?? {}), ...patch } }));
  };

  const usingDefault = maxMatches === '' || maxMatches == null;
  // Reported rather than silently dropped: an out-of-range share used to be
  // coerced to null and omitted, creating an alert with no guard at all and
  // saying nothing. `min`/`max` on a number input do not stop a typed value.
  const guardProblems = validateGuardInputs(formData);

  return (
    <div className="alert-guard-block">
      <h4 className="alert-threshold-heading">Volume guard</h4>

      <p className="alert-guard-intro">
        A guard warns when a run matches an implausible number of subjects —
        the signature of a broken comparison rather than a real event.
      </p>

      <div className="alert-field-row">
        <div className="resui-field">
          <label className="resui-field-label" htmlFor="alert-guard-matches">
            Maximum matched subjects <span className="resui-field-optional">(optional)</span>
          </label>
          <input
            id="alert-guard-matches"
            type="number"
            min="1"
            className="resui-input"
            /* Placeholder, never a value — see the note above. */
            placeholder={`Defaults to ${VOLUME_GUARD_DEFAULT_MAX_MATCHES}`}
            value={maxMatches ?? ''}
            onChange={(e) => setAlertField({ volumeGuardMaxMatches: e.target.value })}
          />
          <span className="resui-field-helper">
            {usingDefault
              ? `Left empty, so the default of ${VOLUME_GUARD_DEFAULT_MAX_MATCHES} applies at evaluation.`
              : 'An explicit cap, stored on the alert.'}
          </span>
        </div>

        <div className="resui-field">
          <label className="resui-field-label" htmlFor="alert-guard-share">
            Maximum share of population <span className="resui-field-optional">(optional)</span>
          </label>
          <div className="alert-percent-input">
            <input
              id="alert-guard-share"
              type="number"
              min="1"
              max="100"
              className="resui-input"
              placeholder="e.g. 50"
              value={maxSharePercent ?? ''}
              onChange={(e) => setAlertField({ volumeGuardMaxShare: e.target.value })}
            />
            <span className="alert-percent-suffix">%</span>
          </div>
          <span className="resui-field-helper">
            A share rule cannot apply to every comparison. When it does not, the
            dry run says so rather than reporting a pass.
          </span>
        </div>
      </div>

      <AlertSpecProblems problems={guardProblems} heading="Check the volume guard" />
    </div>
  );
};

export default AlertGuardFields;
